import { useState } from 'react'
import { cn } from '#app/utils/misc'

export interface ProfileEntryPointBenefit {
	title: string
	description: string
}

export interface ProfileEntryPointItem {
	value?: string
	label: string
	title: string
	situation: string
	benefits: ProfileEntryPointBenefit[]
	ctaLabel?: string
	ctaHref?: string
	badgeLabel?: string
}

export interface ProfileEntryPointsProps {
	eyebrow?: string
	title?: string
	subtitle?: string
	items?: ProfileEntryPointItem[]
	background?: 'default' | 'muted' | 'primary-light'
	defaultValue?: string
}

const backgroundStyles: Record<
	NonNullable<ProfileEntryPointsProps['background']>,
	string
> = {
	default: 'bg-background',
	muted: 'bg-muted/30',
	'primary-light': 'bg-primary/5',
}

function getProfileValue(item: ProfileEntryPointItem, index: number) {
	return item.value ?? `${item.label}-${index}`
}

export function ProfileEntryPoints({
	eyebrow,
	title,
	subtitle,
	items = [],
	background = 'default',
	defaultValue: defaultValueProp,
}: ProfileEntryPointsProps) {
	const [activeIndex, setActiveIndex] = useState(() => {
		if (!defaultValueProp || items.length === 0) return 0
		const idx = items.findIndex(
			(item, i) => getProfileValue(item, i) === defaultValueProp,
		)
		return idx === -1 ? 0 : idx
	})

	if (items.length === 0) return null

	const current = items[activeIndex] ?? items[0]!

	return (
		<section
			className={cn(
				'px-6 py-20 sm:px-8 lg:px-12',
				backgroundStyles[background],
			)}
		>
			<div className="mx-auto flex max-w-7xl flex-col gap-12 lg:gap-16">
				{(eyebrow || title || subtitle) && (
					<header className="flex max-w-2xl flex-col gap-4">
						{eyebrow ? (
							<span className="text-label text-muted-foreground">
								{eyebrow}
							</span>
						) : null}
						{title ? <h2 className="text-title-2">{title}</h2> : null}
						{subtitle ? (
							<p className="text-muted-foreground text-[15px] leading-relaxed whitespace-pre-line md:text-base">
								{subtitle}
							</p>
						) : null}
					</header>
				)}

				<div className="grid gap-8 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start lg:gap-14 xl:grid-cols-[340px_minmax(0,1fr)] xl:gap-16">
					<ol className="border-primary/10 divide-primary/10 m-0 flex list-none flex-col divide-y border-y p-0">
						{items.map((item, idx) => {
							const isActive = idx === activeIndex
							const itemId = getProfileValue(item, idx)
							return (
								<li key={itemId}>
									<button
										type="button"
										aria-expanded={isActive}
										aria-controls={`profile-entry-panel-${itemId}`}
										onClick={() => setActiveIndex(idx)}
										className="group flex w-full flex-col text-left"
									>
										<span className="grid grid-cols-[8px_minmax(0,1fr)_20px] items-center gap-3 py-4">
											<span
												className={cn(
													'size-2 shrink-0 rounded-full transition-all duration-300',
													isActive ? 'bg-gold' : 'bg-primary/20',
												)}
											/>
											<span
												className={cn(
													'flex-1 text-[15px] leading-tight font-semibold transition-colors',
													isActive
														? 'text-primary'
														: 'text-muted-foreground group-hover:text-muted-foreground',
												)}
											>
												{item.label}
											</span>
											<span
												className={cn(
													'text-muted-foreground shrink-0 text-lg leading-none transition-transform duration-300',
													isActive && 'text-gold-text rotate-45',
												)}
												aria-hidden
											>
												+
											</span>
										</span>
									</button>
									<div
										id={`profile-entry-panel-${itemId}`}
										className={cn(
											'grid transition-[grid-template-rows,opacity] duration-300 ease-out lg:hidden',
											isActive
												? 'grid-rows-[1fr] opacity-100'
												: 'grid-rows-[0fr] opacity-0',
										)}
									>
										<div className="min-h-0 overflow-hidden">
											<div className="pb-6">
												<ProfilePanelContent item={item} />
											</div>
										</div>
									</div>
								</li>
							)
						})}
					</ol>

					<div className="hidden lg:sticky lg:top-20 lg:block lg:self-start">
						<div
							key={activeIndex}
							className="animate-in fade-in slide-in-from-bottom-1 duration-200 ease-out"
						>
							<ProfilePanelContent item={current} />
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

interface ProfilePanelContentProps {
	item: ProfileEntryPointItem
}

function ProfilePanelContent({ item }: ProfilePanelContentProps) {
	return (
		<div>
			<h3 className="text-title-3 max-w-[30ch]">{item.title}</h3>
			<p className="text-muted-foreground mt-5 max-w-[68ch] text-[15px] leading-relaxed md:text-base">
				{item.situation}
			</p>

			<p className="text-label text-muted-foreground mt-8 mb-3">Qué cambia</p>

			<ol className="m-0 flex list-none flex-col p-0">
				{item.benefits.map((benefit, index) => (
					<li
						key={`${benefit.title}-${index}`}
						className={cn(
							'grid grid-cols-[32px_minmax(0,1fr)] items-baseline gap-4 py-4',
							index > 0 && 'border-primary/10 border-t',
						)}
					>
						<span className="text-muted-foreground pt-[3px] font-mono text-[11px] font-semibold tracking-[0.05em]">
							{String(index + 1).padStart(2, '0')}
						</span>
						<div>
							<h4 className="text-primary text-[15px] leading-snug font-semibold">
								{benefit.title}
							</h4>
							<p className="text-muted-foreground mt-2 max-w-[68ch] text-sm leading-relaxed">
								{benefit.description}
							</p>
						</div>
					</li>
				))}
			</ol>

			{item.badgeLabel ? (
				<span className="text-label border-primary/15 bg-primary/5 text-muted-foreground mt-8 inline-flex rounded-md border px-2.5 py-1.5">
					{item.badgeLabel}
				</span>
			) : null}
		</div>
	)
}
